import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search } from "lucide-react";
import AdminOrderDrawer from "@/pages/admin/AdminOrderDrawer";
import AdminFarmDrawer from "@/pages/admin/AdminFarmDrawer";

const modeOf = (o) => {
  const s = o.session_id || o.stripe_session_id || "";
  return s.startsWith("cs_live_") ? "live" : s.startsWith("cs_test_") ? "test" : null;
};

export default function AdminOrdersTab() {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [farmId, setFarmId] = useState(null);

  useEffect(() => {
    api.get("/admin/orders").then(r => setOrders(r.data)).catch(()=> setOrders([]));
  }, []);

  const filtered = (orders || []).filter(o => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return o.id?.toLowerCase().includes(needle)
      || o.drop_slug?.toLowerCase().includes(needle)
      || (o.items || []).some(i => i.name?.toLowerCase().includes(needle));
  });

  if (orders === null) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="admin-orders-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-orders-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">All orders</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">{filtered.length} of {orders.length} orders</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search id, drop, item" className="input-base pl-9 text-sm w-64" data-testid="admin-orders-search"/>
        </div>
      </div>

      <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="admin-orders">
        {filtered.length === 0 && <p className="p-8 text-sm text-[var(--ff-text-muted)]">No orders match the search.</p>}
        {filtered.map(o => {
          const mode = modeOf(o);
          return (
          <button key={o.id} onClick={() => setOpenId(o.id)} className="w-full text-left p-5 flex justify-between items-center hover:bg-stone-50 transition-colors" data-testid={`admin-order-${o.id}`}>
            <div>
              <p className="font-mono text-xs text-[var(--ff-text-secondary)]">#{o.id.slice(0, 12)}</p>
              <p className="text-sm font-medium mt-1">{(o.items || []).map(i => i.name).join(", ")}</p>
              <p className="text-xs text-[var(--ff-text-muted)] mt-1">{new Date(o.created_at).toLocaleString()} · {o.items?.length || 0} item{o.items?.length === 1 ? "" : "s"} · {o.drop_slug || "—"}</p>
            </div>
            <div className="text-right">
              <p className="font-semibold">${(o.total || 0).toFixed(2)}</p>
              <div className="flex items-center gap-2 justify-end mt-1">
                {mode === "test" && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold">Test</span>}
                {mode === "live" && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-semibold">Live</span>}
                <span className="text-xs uppercase tracking-wider text-[var(--ff-primary)]">{o.fulfillment_status || o.status}</span>
              </div>
            </div>
          </button>
          );
        })}
      </div>

      <AdminOrderDrawer orderId={openId} onClose={() => setOpenId(null)} onOpenFarm={(vid) => { setOpenId(null); setFarmId(vid); }} />
      <AdminFarmDrawer vendorId={farmId} onClose={() => setFarmId(null)} />
    </div>
  );
}
